// A composed-forecast source's weight is a plain multiplier on its values — a unit bridge (kcfs → cfs = 1000,
// feet → metres = 0.3048). Every place a custom view reads a source's values applies it, so the chart, the card,
// the accuracy score and the REST API (mirrored in erodeapifunction/src/composedForecasts.js weightCsv) agree.

/** A stored weight as the multiplier to apply: missing or unreadable → 1 (unchanged); 0 and negatives are kept. */
export const normalizeWeight = (weight: unknown): number => {
  if (weight === null || weight === undefined || weight === "") return 1;
  const n = Number(weight);
  return Number.isFinite(n) ? n : 1;
};

/** Whether a weight changes anything. */
export const isWeighted = (weight: unknown): boolean => normalizeWeight(weight) !== 1;

/** One value times the weight; non-numbers pass through unchanged. */
export const applyWeight = <T,>(value: T, weight: unknown): T => {
  const w = normalizeWeight(weight);
  return typeof value === "number" && w !== 1 ? ((value * w) as unknown as T) : value;
};

/** `{ timestamp, value }` records with every value weighted. */
export const weightRecords = <R extends { value: number }>(records: R[], weight: unknown): R[] => {
  if (!isWeighted(weight)) return records;
  return records.map((r) => ({ ...r, value: applyWeight(r.value, weight) }));
};

/** Sparkline points (value and its optional low/high band) weighted. A negative weight swaps the band's edges. */
export const weightPoints = <P extends { value: number; low?: number; high?: number }>(points: P[], weight: unknown): P[] => {
  if (!isWeighted(weight)) return points;
  const w = normalizeWeight(weight);
  return points.map((p) => {
    const low = typeof p.low === "number" ? p.low * w : p.low;
    const high = typeof p.high === "number" ? p.high * w : p.high;
    const swap = w < 0 && typeof low === "number" && typeof high === "number";
    return { ...p, value: p.value * w, low: swap ? high : low, high: swap ? low : high };
  });
};

/**
 * A processed-model forecast's per-timestamp rows ({ ts: { "0.5": 12, mean: 11, … } }) with every number weighted.
 * Used for the base model's percentile bands and mean.
 */
export const weightModelData = <D extends Record<string, Record<string, unknown>>>(modelData: D, weight: unknown): D => {
  if (!modelData || !isWeighted(weight)) return modelData;
  const w = normalizeWeight(weight);
  const out: Record<string, unknown> = {};
  for (const [ts, row] of Object.entries(modelData)) {
    // A file can carry a bare number per timestamp instead of a row of series
    if (typeof row === "number") {
      out[ts] = row * w;
      continue;
    }
    if (!row || typeof row !== "object") {
      out[ts] = row;
      continue;
    }
    const next: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      next[key] = typeof value === "number" ? value * w : value;
    }
    out[ts] = next;
  }
  return out as D;
};

/** A source preview ({ values, series[].values }) with every value weighted — the Ops tiles read sources this way. */
export const weightPreviewData = <P extends { values: number[]; series?: Array<{ values: number[] }> }>(preview: P, weight: unknown): P => {
  if (!preview || !isWeighted(weight)) return preview;
  const w = normalizeWeight(weight);
  const scale = (values: number[]) => values.map((v) => (typeof v === "number" ? v * w : v));
  return {
    ...preview,
    values: scale(preview.values || []),
    ...(preview.series ? { series: preview.series.map((s) => ({ ...s, values: scale(s.values || []) })) } : {}),
  };
};
